import { Box, Grid, Typography } from "@mui/material";
import { useEffect } from "react";
import builder from "../../builder";
import RadioGroupButtons from "../../common/RadioGroup/RadioGroupButtons";

const RadioCustomOptions = ({
  field,
  state,
  setState,
  errors,
  register,
  clearErrors,
  control,
  fields,
  isEdit,
  title,
}: any) => {
  const defaultRadioValue = field?.default_value || field?.options?.[0]?.value || "";
  const rawParentValue = state?.[field?.name];
  const isParentObject = rawParentValue !== null && typeof rawParentValue === "object";
  // The parent value is stored as { value, data }. Fall back to a bare string
  // for backward compatibility with data saved before dependents were nested.
  const currentStateValue = isParentObject ? rawParentValue?.value : rawParentValue;
  const dependentData = (isParentObject ? rawParentValue?.data : {}) || {};
  const hasValidStateValue = field?.options?.some((option) => option?.value === currentStateValue);
  const selectedValue = hasValidStateValue ? currentStateValue : defaultRadioValue;
  const dependentItems = field?.dependent_items || [];

  const getActiveDependentItem = (value) => {
    const mappedItem = dependentItems?.find(
      (item) => item?.radio_value === value || item?.value === value,
    );

    if (mappedItem) {
      return mappedItem;
    }

    return dependentItems?.[0] || null;
  };

  const activeItem = getActiveDependentItem(selectedValue);
  const activeFields = activeItem?.fields || (activeItem?.field ? [activeItem.field] : []);

  useEffect(() => {
    if ((!currentStateValue || !hasValidStateValue) && defaultRadioValue) {
      setState((prevState) => {
        const prevParent = prevState?.[field?.name];
        const prevData =
          (prevParent !== null && typeof prevParent === "object" ? prevParent?.data : {}) || {};
        return {
          ...prevState,
          [field?.name]: { value: defaultRadioValue, data: prevData },
        };
      });
    }
  }, [currentStateValue, defaultRadioValue, field?.name, hasValidStateValue, setState]);

  const handleRadioChange = (event) => {
    const nextValue = event.target.value;
    const nextActiveItem = getActiveDependentItem(nextValue);
    const nextActiveFields =
      nextActiveItem?.fields || (nextActiveItem?.field ? [nextActiveItem.field] : []);

    // Carry over values for fields that stay active; anything belonging to the
    // previously active option is dropped so parent.data only holds live fields.
    const nextData = nextActiveFields.reduce((acc: Record<string, any>, activeField: any) => {
      if (activeField?.name) {
        acc[activeField.name] =
          dependentData?.[activeField.name] ?? (activeField?.multiple ? [] : "");
      }
      return acc;
    }, {});

    setState({
      ...state,
      [field?.name]: { value: nextValue, data: nextData },
    });
    clearErrors?.(field?.name);
  };

  // Scoped setState handed to builder for the dependent fields: every dependent
  // value is written into parent.data instead of the flat top-level state.
  const setDependentState = (updatedData: any) => {
    setState((prevState: any) => {
      const prevParent = prevState?.[field?.name];
      const isObj = prevParent !== null && typeof prevParent === "object";
      const prevValue = isObj ? prevParent?.value : prevParent;
      const prevData = (isObj ? prevParent?.data : {}) || {};
      const resolvedData = typeof updatedData === "function" ? updatedData(prevData) : updatedData;
      return {
        ...prevState,
        [field?.name]: {
          value: prevValue ?? selectedValue,
          data: resolvedData,
        },
      };
    });
  };

  return (
    <Box>
      <RadioGroupButtons
        value={field?.options}
        field={field}
        row={true}
        handleChange={handleRadioChange}
        error={errors?.[field?.name]}
        control={control}
        clearErrors={clearErrors}
        data={selectedValue}
      />
      {activeFields?.length > 0 ? (
        <Grid container spacing={2} sx={{ mt: 4 }}>
          <Grid item xs={12}>
            <Typography variant='p4regular'>
              {activeItem?.description || activeFields?.[0]?.description}
            </Typography>
          </Grid>
          {activeFields?.map((activeField) => (
            <Grid item xs={12} key={activeField?.name}>
              {builder(
                activeField,
                dependentData,
                errors,
                register,
                clearErrors,
                setDependentState,
                fields,
                isEdit,
                title,
                control,
                null,
              )}
            </Grid>
          ))}
        </Grid>
      ) : null}
    </Box>
  );
};

export default RadioCustomOptions;
